<template>
  <div class="kind-upload-folder-group" :style="{ marginLeft: depth === 0 ? '0' : '1.5rem' }">
    <div class="kind-upload-folder-heading small font-weight-bold text-muted mb-2 mt-2">
      <i class="fa-solid fa-folder mr-1"></i>{{ node.name }}
      <span class="text-muted font-weight-normal">({{ folderCountText }})</span>
      <span v-if="existenceState === 'existing'" class="badge badge-secondary ml-1">{{ __('video.folder_upload_badge_existing') }}</span>
      <span v-else-if="existenceState === 'new'" class="badge badge-info ml-1">{{ __('video.folder_upload_badge_new') }}</span>
    </div>

    <div v-for="item in node.items" :key="item._uuid" class="kind-upload-item card mb-3 shadow-sm">
      <div class="card-header bg-transparent d-flex align-items-center py-2 px-3 flex-wrap">
        <div class="mr-3">
          <span class="badge badge-light">{{ kindLabelOf(item.kind) }}</span>
        </div>
        <div class="d-flex align-items-center mr-3">
          <div class="custom-control custom-radio custom-control-inline">
            <input
              class="custom-control-input"
              type="radio"
              :id="'storage_hot_' + item._uuid"
              :value="storageHotId"
              :disabled="busy || item.delete_source"
              v-model.number="item.storage_id"
            />
            <label
              class="custom-control-label small"
              :for="'storage_hot_' + item._uuid"
              :class="{ 'text-muted': item.delete_source }"
            >{{ __('video.hot') }}</label>
          </div>
          <div class="custom-control custom-radio custom-control-inline">
            <input
              class="custom-control-input"
              type="radio"
              :id="'storage_cold_' + item._uuid"
              :value="storageColdId"
              v-model.number="item.storage_id"
              :disabled="busy || isColdDisabled(item) || item.delete_source"
            />
            <label
              class="custom-control-label small"
              :for="'storage_cold_' + item._uuid"
              :class="{ 'text-muted': isColdDisabled(item) || item.delete_source }"
            >{{ __('video.cold') }}</label>
          </div>
        </div>

        <span class="small mr-2" :class="statusClass(item.status)">
          <i :class="statusIcon(item.status)" class="mr-1"></i>{{ statusLabel(item.status) }}
          <template v-if="item.status === 'uploading'"> {{ item.percent }}%</template>
        </span>

        <span v-if="item.register_status" class="small mr-2" :class="registerStatusClass(item.register_status)">
          <i :class="registerStatusIcon(item.register_status)" class="mr-1"></i>{{ registerStatusLabel(item.register_status) }}
        </span>

        <button
          v-if="(item.status === 'pending' || item.status === 'failed' || item.register_status === 'register_failed') && !busy"
          type="button"
          class="btn btn-link text-danger p-0 ml-auto"
          @click="$emit('remove-item', item)"
        >
          <i class="fa-solid fa-trash-can"></i>
        </button>
      </div>

      <div class="card-body px-3 py-2">
        <div v-if="isSourceDeleteEligible(item)" class="mb-2 p-2 border rounded">
          <div class="custom-control custom-checkbox">
            <input
              type="checkbox"
              class="custom-control-input"
              :id="'delete_source_' + item._uuid"
              v-model="item.delete_source"
              :disabled="busy"
              @change="$emit('delete-source-change', item)"
            />
            <label class="custom-control-label small" :for="'delete_source_' + item._uuid">{{ __('video.source_delete_upload_label') }}</label>
          </div>
          <small class="form-text text-muted mb-0">{{ __('video.source_delete_upload_warning') }}</small>
        </div>

        <div class="small">
          <i class="fa-regular fa-file mr-1 text-muted"></i>
          <span class="font-weight-bold">{{ item.original_name }}</span>
          <span class="text-muted ml-2">{{ formatSize(item.filesize) }}</span>
        </div>

        <div v-if="item.status === 'uploading'" class="progress mt-2" style="height: 6px;">
          <div
            class="progress-bar"
            role="progressbar"
            :style="{ width: item.percent + '%' }"
            :aria-valuenow="item.percent"
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>

        <div v-if="item.status === 'failed' && item.error" class="text-danger small mt-2">{{ item.error }}</div>
        <div v-if="item.register_status === 'register_failed' && item.register_error" class="text-danger small mt-2">{{ item.register_error }}</div>
      </div>
    </div>

    <kind-upload-folder-group
      v-for="child in node.children"
      :key="child.path"
      :node="child"
      :depth="depth + 1"
      :busy="busy"
      :storage-hot-id="storageHotId"
      :storage-cold-id="storageColdId"
      :cold-min-bytes="coldMinBytes"
      :kind-labels="kindLabels"
      :folder-existence-map="folderExistenceMap"
      @remove-item="$emit('remove-item', $event)"
      @delete-source-change="$emit('delete-source-change', $event)"
    />
  </div>
</template>

<script>
// 元ファイル削除チェックボックスの対象拡張子(KindUploadListComponent.vue の
// SOURCE_DELETE_EXTS と同一定義。再帰コンポーネント単体で完結させるためコピーして持つ)
const SOURCE_DELETE_EXTS = ['mxf', 'mov', 'mts', 'mp4'];

export default {
  name: 'KindUploadFolderGroup',
  emits: ['remove-item', 'delete-source-change'],
  props: {
    node: { type: Object, required: true },
    depth: { type: Number, default: 0 },
    busy: { type: Boolean, default: false },
    storageHotId: { type: Number, required: true },
    storageColdId: { type: Number, required: true },
    coldMinBytes: { type: Number, default: 0 },
    kindLabels: { type: Object, default: () => ({}) },
    folderExistenceMap: { type: Object, default: () => ({}) },
  },
  computed: {
    folderCountText() {
      return this.__('video.folder_upload_folder_count').replace(':count', String(this.node.items.length));
    },
    existenceState() {
      return this.folderExistenceMap[this.node.path] || null;
    },
  },
  methods: {
    extractExtension(name) {
      const m = (name || '').match(/(.*)(?:\.([^.]+$))/);
      return m ? m[2] : '';
    },
    isSourceDeleteEligible(item) {
      const ext = this.extractExtension(item.original_name).toLowerCase();
      return SOURCE_DELETE_EXTS.includes(ext);
    },
    isColdDisabled(item) {
      if (!item) return false;
      return this.coldMinBytes > 0 && item.filesize > 0 && item.filesize < this.coldMinBytes;
    },
    kindLabelOf(kind) {
      return this.kindLabels[kind] || kind || '';
    },
    formatSize(bytes) {
      const n = Number(bytes) || 0;
      if (n < 1024) return `${n} B`;
      if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
      if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
      return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
    },
    statusLabel(status) {
      const map = {
        pending: this.__('video.upload_status_pending'),
        uploading: this.__('video.upload_status_uploading'),
        success: this.__('video.upload_status_success'),
        failed: this.__('video.upload_status_failed'),
      };
      return map[status] || '';
    },
    statusIcon(status) {
      const map = {
        pending: 'fa-regular fa-clock',
        uploading: 'fa-solid fa-spinner fa-spin',
        success: 'fa-solid fa-circle-check',
        failed: 'fa-solid fa-circle-xmark',
      };
      return map[status] || '';
    },
    statusClass(status) {
      const map = {
        pending: 'text-muted',
        uploading: 'text-primary',
        success: 'text-success',
        failed: 'text-danger',
      };
      return map[status] || '';
    },
    registerStatusLabel(status) {
      const map = {
        registering: this.__('video.folder_upload_status_registering'),
        registered: this.__('video.folder_upload_register_done'),
        register_failed: this.__('video.folder_upload_register_failed'),
      };
      return map[status] || '';
    },
    registerStatusIcon(status) {
      const map = {
        registering: 'fa-solid fa-spinner fa-spin',
        registered: 'fa-solid fa-circle-check',
        register_failed: 'fa-solid fa-circle-xmark',
      };
      return map[status] || '';
    },
    registerStatusClass(status) {
      const map = {
        registering: 'text-primary',
        registered: 'text-success',
        register_failed: 'text-danger',
      };
      return map[status] || '';
    },
  },
};
</script>
